/**
 * Watchlist Zustand Store
 *
 * Persistent store for user's favorite pairs, arbitrages, and balancers.
 * Uses localStorage via Zustand's persist middleware.
 * Uses composite keys (projectId-itemId) to prevent ID collisions.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { createWatchlistKey, type WatchlistStore } from './types';

const STORAGE_KEY = 'mm-watchlist-v2'; // New key to avoid migration issues

export const useWatchlistStore = create<WatchlistStore>()(
  persist(
    (set, get) => ({
      // State
      favoritePairs: [],
      favoriteArbitrages: [],
      favoriteBalancers: [],
      favoriteProjects: [],

      // Actions
      toggleFavoritePair: (projectId: number, pairId: number) => {
        const key = createWatchlistKey(projectId, pairId);
        set(state => ({
          favoritePairs: state.favoritePairs.includes(key)
            ? state.favoritePairs.filter(x => x !== key)
            : [...state.favoritePairs, key],
        }));
      },

      toggleFavoriteArbitrage: (projectId: number, arbitrageId: number) => {
        const key = createWatchlistKey(projectId, arbitrageId);
        set(state => ({
          favoriteArbitrages: state.favoriteArbitrages.includes(key)
            ? state.favoriteArbitrages.filter(x => x !== key)
            : [...state.favoriteArbitrages, key],
        }));
      },

      toggleFavoriteBalancer: (projectId: number, balancerId: number) => {
        const key = createWatchlistKey(projectId, balancerId);
        set(state => ({
          favoriteBalancers: state.favoriteBalancers.includes(key)
            ? state.favoriteBalancers.filter(x => x !== key)
            : [...state.favoriteBalancers, key],
        }));
      },

      toggleFavoriteProject: (projectId: number) => {
        const key = String(projectId);
        set(state => ({
          favoriteProjects: state.favoriteProjects.includes(key)
            ? state.favoriteProjects.filter(x => x !== key)
            : [...state.favoriteProjects, key],
        }));
      },

      addProjectItems: items => {
        const projectKey = String(items.projectId);
        set(state => ({
          favoritePairs: [...new Set([...state.favoritePairs, ...items.pairKeys])],
          favoriteArbitrages: [...new Set([...state.favoriteArbitrages, ...items.arbitrageKeys])],
          favoriteBalancers: [...new Set([...state.favoriteBalancers, ...items.balancerKeys])],
          favoriteProjects: state.favoriteProjects.includes(projectKey)
            ? state.favoriteProjects
            : [...state.favoriteProjects, projectKey],
        }));
      },

      removeProjectItems: items => {
        const projectKey = String(items.projectId);
        set(state => ({
          favoritePairs: state.favoritePairs.filter(key => !items.pairKeys.includes(key)),
          favoriteArbitrages: state.favoriteArbitrages.filter(
            key => !items.arbitrageKeys.includes(key),
          ),
          favoriteBalancers: state.favoriteBalancers.filter(
            key => !items.balancerKeys.includes(key),
          ),
          favoriteProjects: state.favoriteProjects.filter(key => key !== projectKey),
        }));
      },

      isFavoritePair: (projectId: number, pairId: number) =>
        get().favoritePairs.includes(createWatchlistKey(projectId, pairId)),

      isFavoriteArbitrage: (projectId: number, arbitrageId: number) =>
        get().favoriteArbitrages.includes(createWatchlistKey(projectId, arbitrageId)),

      isFavoriteBalancer: (projectId: number, balancerId: number) =>
        get().favoriteBalancers.includes(createWatchlistKey(projectId, balancerId)),

      isFavoriteProject: (projectId: number) => get().favoriteProjects.includes(String(projectId)),

      clearAll: () => {
        set({
          favoritePairs: [],
          favoriteArbitrages: [],
          favoriteBalancers: [],
          favoriteProjects: [],
        });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: state => ({
        favoritePairs: state.favoritePairs,
        favoriteArbitrages: state.favoriteArbitrages,
        favoriteBalancers: state.favoriteBalancers,
        favoriteProjects: state.favoriteProjects,
      }),
    },
  ),
);

// Selector hooks for optimized re-renders
export const useFavoritePairs = () => useWatchlistStore(s => s.favoritePairs);
export const useFavoriteArbitrages = () => useWatchlistStore(s => s.favoriteArbitrages);
export const useFavoriteBalancers = () => useWatchlistStore(s => s.favoriteBalancers);

export const useIsFavoritePair = (projectId: number, pairId: number) =>
  useWatchlistStore(s => s.favoritePairs.includes(createWatchlistKey(projectId, pairId)));
export const useIsFavoriteArbitrage = (projectId: number, arbitrageId: number) =>
  useWatchlistStore(s => s.favoriteArbitrages.includes(createWatchlistKey(projectId, arbitrageId)));
export const useIsFavoriteBalancer = (projectId: number, balancerId: number) =>
  useWatchlistStore(s => s.favoriteBalancers.includes(createWatchlistKey(projectId, balancerId)));

export const useToggleFavoritePair = () => useWatchlistStore(s => s.toggleFavoritePair);
export const useToggleFavoriteArbitrage = () => useWatchlistStore(s => s.toggleFavoriteArbitrage);
export const useToggleFavoriteBalancer = () => useWatchlistStore(s => s.toggleFavoriteBalancer);
